import Swiper from "./swiper-bundle.esm.browser.min.js";

const MOBILE_BREAKPOINT = 769;

class MuscleGutSection extends HTMLElement {
  constructor() {
    super();
    this._swiper = null;
    this._resizeTimer = null;
    this._initialized = false;
    this._handleResize = this._handleResize.bind(this);
    this._handleSectionLoad = this._handleSectionLoad.bind(this);
    this._handleSectionUnload = this._handleSectionUnload.bind(this);
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;
    document.addEventListener("shopify:section:load", this._handleSectionLoad);
    document.addEventListener("shopify:section:unload", this._handleSectionUnload);
    window.addEventListener("resize", this._handleResize, { passive: true });
    this._updateLayout();
  }

  disconnectedCallback() {
    this._initialized = false;
    this._destroySwiper();
    window.removeEventListener("resize", this._handleResize);
    document.removeEventListener("shopify:section:load", this._handleSectionLoad);
    document.removeEventListener("shopify:section:unload", this._handleSectionUnload);
    if (this._resizeTimer) {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = null;
    }
  }

  _isOurSection(event) {
    const section = this.closest(".shopify-section");
    return section && (event.target === section || section.contains(event.target));
  }

  _handleSectionLoad(event) {
    if (!this._isOurSection(event)) return;
    this._destroySwiper();
    this._updateLayout();
  }

  _handleSectionUnload(event) {
    if (!this._isOurSection(event)) return;
    this._destroySwiper();
  }

  _readConfig() {
    const slideCount = parseInt(this.dataset.slideCount || "0", 10);
    return {
      slideCount,
      sectionId: this.dataset.sectionId || "",
      spaceBetween: parseInt(this.dataset.spaceBetween || "16", 10),
      mobileSlidesPerView: parseFloat(this.dataset.slidesPerViewMobile || "1.3"),
      desktopSlidesPerView: parseFloat(this.dataset.slidesPerViewDesktop || "1.5"),
      desktopSliderThreshold: parseInt(this.dataset.desktopSliderThreshold || "1", 10),
      showArrows: this.dataset.showArrows === "true",
      showPagination: this.dataset.showPagination === "true",
      enableLoop: this.dataset.enableLoop === "true",
      enableAutoplay: this.dataset.enableAutoplay === "true",
      autoplaySpeed: parseInt(this.dataset.autoplaySpeed || "5000", 10),
      pauseOnHover: this.dataset.pauseOnHover !== "false",
      pauseOnInteraction: this.dataset.pauseOnInteraction === "true",
    };
  }

  _isMobile() {
    return window.innerWidth < MOBILE_BREAKPOINT;
  }

  _shouldUseSwiper(config) {
    if (config.slideCount <= 0) return false;
    if (this._isMobile()) return true;
    return config.slideCount > config.desktopSliderThreshold;
  }

  _getSlidesPerView(config) {
    const requested = this._isMobile()
      ? config.mobileSlidesPerView
      : config.desktopSlidesPerView;
    return Math.min(requested, config.slideCount);
  }

  _getSlideWidth() {
    const value = getComputedStyle(this).getPropertyValue("--mgs-slide-width").trim();
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 320;
  }

  _getSlideOffsets() {
    const swiperEl = this.querySelector("[data-swiper]");
    if (!swiperEl) {
      return { slidesOffsetBefore: 0, slidesOffsetAfter: 0 };
    }

    const containerWidth = swiperEl.clientWidth;
    const slideWidth = this._getSlideWidth();
    const slidesOffsetAfter = Math.max(0, containerWidth - slideWidth);

    return { slidesOffsetBefore: 0, slidesOffsetAfter };
  }

  _applySlideOffsets(swiper) {
    if (!swiper || swiper.destroyed) return;

    const offsets = this._getSlideOffsets();
    swiper.params.slidesOffsetBefore = offsets.slidesOffsetBefore;
    swiper.params.slidesOffsetAfter = offsets.slidesOffsetAfter;
  }

  _cleanupSwiperClasses() {
    const swiperEl = this.querySelector("[data-swiper]");
    const container = this.querySelector("[data-swiper-container]");
    const slides = this.querySelectorAll("[data-swiper-slide]");

    if (swiperEl) {
      swiperEl.classList.remove("swiper", "mgs__swiper", "swiper-initialized");
    }
    if (container) {
      container.classList.remove("swiper-wrapper");
    }
    slides.forEach((slide) => slide.classList.remove("swiper-slide"));
  }

  _applyStaticMode(config) {
    this.classList.add("mgs--static");
    this.classList.remove("mgs--swiper");
    this._cleanupSwiperClasses();
    this.style.setProperty(
      "--mgs-static-columns",
      String(Math.min(config.slideCount, 2)),
    );
    this._syncSlideAccessibility(0);
  }

  _prepareSwiperStructure() {
    const swiperEl = this.querySelector("[data-swiper]");
    const container = this.querySelector("[data-swiper-container]");
    const slideEls = this.querySelectorAll("[data-swiper-slide]");

    if (!swiperEl || !container || slideEls.length === 0) return null;

    swiperEl.classList.add("swiper", "mgs__swiper");
    container.classList.add("swiper-wrapper");
    slideEls.forEach((slide) => slide.classList.add("swiper-slide"));

    return { slideEls };
  }

  _syncSlideAccessibility(activeIndex) {
    const slides = this.querySelectorAll("[data-swiper-slide]");
    slides.forEach((slide, index) => {
      const isActive = index === activeIndex;
      slide.setAttribute("aria-hidden", isActive ? "false" : "true");
    });
  }

  _initSwiper(config) {
    const structure = this._prepareSwiperStructure();
    if (!structure) return;

    const { slideEls } = structure;
    const slidesPerView = this._getSlidesPerView(config);
    const canNavigate = slideEls.length > slidesPerView;
    const canLoop =
      config.enableLoop && canNavigate && slideEls.length > slidesPerView;

    this.classList.add("mgs--swiper");
    this.classList.remove("mgs--static");

    const slideOffsets = this._getSlideOffsets();

    const swiperConfig = {
      slidesPerView: "auto",
      spaceBetween: config.spaceBetween,
      slidesOffsetBefore: slideOffsets.slidesOffsetBefore,
      slidesOffsetAfter: slideOffsets.slidesOffsetAfter,
      watchOverflow: true,
      grabCursor: canNavigate || slideEls.length > 1,
      speed: 350,
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      a11y: {
        enabled: true,
        prevSlideMessage: "Previous card",
        nextSlideMessage: "Next card",
      },
    };

    if (canLoop) {
      swiperConfig.loop = true;
    }

    if (config.showArrows && slideEls.length > 1) {
      const nextEl = this.querySelector(`.mgs__nav-next--${config.sectionId}`);
      const prevEl = this.querySelector(`.mgs__nav-prev--${config.sectionId}`);
      if (nextEl && prevEl) {
        swiperConfig.navigation = { nextEl, prevEl };
      }
    }

    if (config.showPagination && slideEls.length > 1) {
      const paginationEl = this.querySelector(
        `.mgs__pagination--${config.sectionId}`,
      );
      if (paginationEl) {
        swiperConfig.pagination = {
          el: paginationEl,
          clickable: true,
          renderBullet: (index, className) => {
            const num = index + 1;
            const total = config.slideCount;
            return `<button type="button" class="${className}" aria-label="Go to card ${num} of ${total}"></button>`;
          },
        };
      }
    }

    if (config.enableAutoplay && canNavigate) {
      swiperConfig.autoplay = {
        delay: config.autoplaySpeed,
        disableOnInteraction: config.pauseOnInteraction,
        pauseOnMouseEnter: config.pauseOnHover,
      };
    }

    const existingOn = swiperConfig.on || {};
    swiperConfig.on = {
      ...existingOn,
      afterInit: (swiper) => {
        existingOn.afterInit?.(swiper);
        this._applySlideOffsets(swiper);
        swiper.update();
        this._syncSlideAccessibility(swiper.realIndex ?? swiper.activeIndex);
      },
      slideChangeTransitionEnd: (swiper) => {
        existingOn.slideChangeTransitionEnd?.(swiper);
        this._syncSlideAccessibility(swiper.realIndex ?? swiper.activeIndex);
      },
      resize: (swiper) => {
        existingOn.resize?.(swiper);
        this._applySlideOffsets(swiper);
        swiper.update();
      },
    };

    const swiperEl = this.querySelector("[data-swiper]");
    this._swiper = new Swiper(swiperEl, swiperConfig);
    requestAnimationFrame(() => this._swiper?.update());
  }

  _destroySwiper() {
    if (this._swiper && !this._swiper.destroyed) {
      this._swiper.destroy(true, true);
    }
    this._swiper = null;
    this._cleanupSwiperClasses();
  }

  _updateLayout() {
    const config = this._readConfig();
    const shouldUseSwiper = this._shouldUseSwiper(config);
    const isSwiperActive = Boolean(this._swiper);

    if (!shouldUseSwiper) {
      this._destroySwiper();
      this._applyStaticMode(config);
      return;
    }

    if (isSwiperActive) {
      this._destroySwiper();
    }

    this._initSwiper(config);
  }

  _handleResize() {
    if (this._resizeTimer) clearTimeout(this._resizeTimer);
    this._resizeTimer = setTimeout(() => {
      const config = this._readConfig();
      const shouldUseSwiper = this._shouldUseSwiper(config);
      const isSwiperActive = Boolean(this._swiper);

      if (shouldUseSwiper !== isSwiperActive) {
        this._updateLayout();
        return;
      }

      if (this._swiper && !this._swiper.destroyed) {
        this._applySlideOffsets(this._swiper);
        this._swiper.update();
      }
    }, 150);
  }
}

if (!customElements.get("muscle-gut-section")) {
  customElements.define("muscle-gut-section", MuscleGutSection);
}
